
import { BatchData } from '@/models/batch-data.model';
import { ref, toRef, computed, PropType, onMounted, watch } from 'vue';
import { CouponsWithImageUrl } from '@/lib/api/coupons.api';
import { useAccountStore } from '@/store/useAccount';
import { useCouponStore } from '@/store/useCoupon';
import { useScannerCouponStore } from '@/store/useScannerCoupon';
import { useCouponTypesStore } from '@/store/useCouponTypes';
import { rules, valid } from '@/store/stateless/rules';
import { storeToRefs } from 'pinia';

export default {
  name: 'coupon-options',
  emits: ['valid', 'couponFields'],
  props: {
    item: {
      required: true,
      type: Object as PropType<CouponsWithImageUrl>,
    },
    isDisabled: {
      required: true,
      type: Boolean,
      default: false,
    },
    batchData: {
      type: Object as PropType<BatchData>,
      default: {} as BatchData,
    },
  },
  setup(props, { emit }) {
    const account = useAccountStore();
    const coupon = useCouponStore();
    const scannerCoupon = useScannerCouponStore();
    const couponTypesStore = useCouponTypesStore();

    const { listAccounts } = storeToRefs(account);
    const { error, selectedCoupons } = storeToRefs(coupon);
    const { currentScanner } = storeToRefs(scannerCoupon);
    const { allTypes, getTypeById } = storeToRefs(couponTypesStore);

    const item = toRef(props, 'item');

    const types = computed(() => allTypes.value);

    const form = ref();
    const couponTypeId = ref(item.value.couponTypeId || item.value.couponType?.id);
    const amount = ref(item.value.amount);
    const accountNumber = ref(item.value.accountNumber);
    const couponNumber = ref(item.value.couponNumber);
    const isActive = ref();
    const couponNumberLoading = ref();
    const erpUrl = ref(null);

    onMounted(async () => {
      validate();
    });

    const validate = async () => {
      if (form.value) {
        const result = await form.value.validate();
        const valid = result.valid;
        const validAccount =
          currentType.value  ? true : isActive.value;

        emit('valid', valid && validAccount && vehicleId.value && driverId.value);

        if (valid) {
          const fields = {
            couponTypeId: couponTypeId.value,
            amount: amount.value,
            accountNumber: accountNumber.value,
            couponNumber: couponNumber.value,
          };

          emit('couponFields', fields);
        }

        return valid;
      }
    };

    const validateOptions = (params, value) => {
      const valid = !rules.coupon[params]?.some((fn) => typeof fn(value) === 'string');
      if (valid && value !== item.value[params]) {
        coupon.saveField(item.value.id, params, value);
        validate();
        if (params === 'couponTypeId') {
          item.value.couponTypeId = value;
        } else {
          item.value[params] = value;
        }
      }
    };

    const currentType = computed(() => {
      const id = couponTypeId.value;
      if (id) {
        return getTypeById.value(id);
      }
      // Fallback to couponType from item if couponTypeId is not set
      return item.value.couponType;
    });

    /**
     * Truthy when the coupon has any resolvable driver id (PG or raw Odoo).
     * Used by `validate()` (line 178) to gate the approve button.
     * Note: the value may be a PG `drivers.id` OR an Odoo `res.partner.id`
     * depending on whether the row was batched via the 200 or 404 path.
     * The form never writes this back to the API.
     */
    const driverId = computed(
      () =>
        item.value.driverId ||
        item.value.odooDriverId ||
        props.batchData.driverId ||
        props.batchData.odooDriverId,
    );

    /** Truthy when the coupon has any resolvable vehicle id. Same dual-semantics caveat as `driverId`. */
    const vehicleId = computed(
      () =>
        item.value.vehicleId ||
        item.value.fleetVehicleId ||
        props.batchData.vehicleId ||
        props.batchData.fleetVehicleId,
    );

    const isChange = ref(false);
    const checkAccountNumber = async () => {
      if (currentType.value?.code !== 'ECR' && currentType.value?.code !== 'CCC') {
        couponNumberLoading.value = true;
        selectedType();
        isChange.value = true;
        validate();
        const result = listAccounts.value.find((acc) => acc.ref === accountNumber.value);
        if (result && result.active) {
          isActive.value = true;
          couponNumberLoading.value = false;
          validateOptions('accountNumber', accountNumber.value);
        } else {
          isActive.value = false;
          couponNumberLoading.value = false;
        }
      } else {
        isActive.value = true;
        validate();
        validateOptions('accountNumber', accountNumber.value);
      }
    };

    const selectedType = () => {
      if (
        currentType.value?.code === 'CHO' &&
        accountNumber.value.toString().substring(0, 1) !== 'H'
      ) {
        accountNumber.value = `H${accountNumber.value}`;
      } else if (
        currentType.value?.code === 'CHO' &&
        accountNumber.value.toString().substring(0, 1) === 'H'
      ) {
        accountNumber.value = `${accountNumber.value}`;
      } else if (currentType.value?.code === 'CCC') {
        accountNumber.value = '6332'; // default account for CCC types
        validateOptions('accountNumber', accountNumber.value); // trigger save account number
      } else {
        accountNumber.value = accountNumber.value.toString().replace('H', '');
      }
    };

    watch(
      item,
      (newItem) => {
        couponTypeId.value = newItem.couponTypeId || newItem.couponType?.id;
        amount.value = newItem.amount;
        couponNumber.value = newItem.couponNumber;
        if (newItem.couponTypeId || newItem.couponType || newItem.accountNumber) {
          accountNumber.value = newItem.accountNumber;
          checkAccountNumber();
        }
      },
      { immediate: true },
    );

    return {
      selectedType,
      selectedCoupons,
      isChange,
      isActive,
      couponNumberLoading,
      error,
      form,
      couponTypeId,
      types,
      currentType,
      amount,
      accountNumber,
      couponNumber,
      rules,
      valid,
      erpUrl,
      currentScanner,
      validate,
      validateOptions,
      checkAccountNumber,
      driverId,
      vehicleId,
    };
  },
};
